import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { H4, P3, useWindowSize } from '@moneymade/moneymade-ui'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

import TooltipPopup from 'modules/discover/PlatformPageNew/Calculator/FeesCalculator/TooltipPopup/TooltipPopup'
import { toNumberWithDecimal, toWithSeparator } from 'utils/getters'
import styles from './BarChartTable.module.scss'

const BarChartTable = ({ form, formResults }) => {
  const { screenWidth } = useWindowSize()

  const barGap = useMemo(() => {
    if (screenWidth > 500) return form?.term > 5 ? '10' : '30'
    return 5
  }, [form?.term, screenWidth])

  const projectedReturn = useMemo(
    () => formResults.chart.reduce((acc, { return: returnVal }) => acc + returnVal, 0),
    [formResults.chart]
  )

  return (
    <div className={styles.BarChartTable}>
      <div className={styles.TableHead}>
        <div className={styles.HeadRow}>
          <div className={styles.Label}>
            <svg width="8" height="8">
              <circle cx="4" cy="4" r="3" stroke="#E8E8EB" strokeWidth="2" fill="#E8E8EB" />
            </svg>
            <P3 weight="light">Invested</P3>
          </div>
          <H4 className={styles.Value}>${toWithSeparator(formResults.amountInvested)}</H4>
        </div>
        <div className={styles.HeadRow}>
          <div className={styles.Label}>
            <svg width="8" height="8">
              <circle cx="4" cy="4" r="3" stroke="#CECED2" strokeWidth="2" fill="#CECED2" />
            </svg>
            <P3 weight="light">Projected Fees</P3>
          </div>
          <H4 className={styles.Value}>${toWithSeparator(formResults.totalFees)}</H4>
        </div>
        <div className={styles.HeadRow}>
          <div className={styles.Label}>
            <svg width="8" height="8">
              <circle cx="4" cy="4" r="3" stroke="#14AEA5" strokeWidth="2" fill="#14AEA5" />
            </svg>
            <P3 weight="light">Projected return</P3>
          </div>
          <H4 className={styles.Value}>${toWithSeparator(projectedReturn.toFixed(2))}</H4>
        </div>
        <div className={styles.HeadRow}>
          <div className={styles.Label}>
            <P3 weight="light">Value after fees</P3>
          </div>

          <H4 className={styles.Value}>${toWithSeparator(formResults.valueAfterFees)}</H4>
        </div>
      </div>

      <div className={styles.Chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={formResults.chart}
            margin={{ top: 0, right: 0, bottom: 0, left: -12 }}
            barCategoryGap={barGap}
          >
            <CartesianGrid strokeDasharray="0" vertical={false} />
            <XAxis dataKey="name" tickLine={false} />
            <YAxis
              type="number"
              domain={[0, 'auto']}
              tickCount={6}
              tickFormatter={tick => toNumberWithDecimal(tick * 5)}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<TooltipPopup />} />
            <Bar name="Investment" dataKey="startVisual" stackId="a" fill="#E8E8EB" />
            <Bar name="Fees this year" dataKey="fees" stackId="a" fill="#CECED2" />
            <Bar name="Return this year" dataKey="return" stackId="a" fill="#14AEA5" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

BarChartTable.propTypes = {
  form: PropTypes.shape({
    investmentAmount: PropTypes.number,
    investmentAmountView: PropTypes.string,
    targetReturn: PropTypes.number,
    targetReturnView: PropTypes.string,
    term: PropTypes.number,
    termView: PropTypes.string,
    fees: PropTypes.number,
    feesView: PropTypes.string,
    feesSwitcher: PropTypes.string
  }),
  formResults: PropTypes.shape({
    amountInvested: PropTypes.number,
    chart: PropTypes.arrayOf(PropTypes.object),
    totalFees: PropTypes.number,
    return: PropTypes.number,
    valueAfterFees: PropTypes.number
  })
}

BarChartTable.defaultProps = {
  form: {
    investmentAmount: 1000,
    investmentAmountView: '1,000',
    targetReturn: 8,
    targetReturnView: '8',
    term: 5,
    termView: '5',
    fees: 1,
    feesView: '1',
    feesSwitcher: '%'
  },
  formResults: {
    amountInvested: 1000,
    chart: [],
    totalFees: 0,
    return: 0,
    valueAfterFees: 0
  }
}

export default BarChartTable
